import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  Button,
  Space,
  Tag,
  Form,
  Input,
  Switch,
  Typography,
  App,
  Card,
  Row,
  Col,
  Tooltip,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { withDevModalFilePath } from '../../components/common';
import { CategoryTagCloudFilter } from '../../components/common/CategoryTagCloudFilter';

const { Modal } = withDevModalFilePath('client/src/pages/WorkOrderTemplates/WorkOrderTemplatesPage.tsx');
import {
  PlusOutlined,
  ReloadOutlined,
  EditOutlined,
  DeleteOutlined,
  SettingOutlined,
  CopyOutlined,
  HolderOutlined,
  LockOutlined,
} from '@ant-design/icons';
import { PermissionButton } from '../../components/common/PermissionButton';
import { usePermission } from '../../hooks/usePermission';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useWorkOrderTemplates, useWorkOrderTemplateMutations } from '../../hooks/useWorkOrderTemplates';
import { WorkOrderTemplateForm } from '../../components/work-order';
import type { ChecklistItem } from '../../../../shared/types/work-order-template.types';
import { trpc } from '../../lib/api';

const { Title } = Typography;

type TemplateRecord = ReturnType<typeof useWorkOrderTemplates>['templates'][number];
type WorkTypeCategorySummary = {
  id: string;
  categoryLabel?: string | null;
  color?: string | null;
  icon?: string | null;
};

const isWorkTypeCategorySummary = (value: unknown): value is WorkTypeCategorySummary => {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const candidate = value as { id?: unknown };
  return typeof candidate.id === 'string';
};

const WORK_TYPE_OPTIONS = [
  { label: '点検', value: 'inspection' },
  { label: '修理', value: 'repair' },
  { label: '交換', value: 'replacement' },
  { label: 'メンテナンス', value: 'maintenance' }
];

const PRIORITY_OPTIONS = [
  { label: '低', value: 'low' },
  { label: '中', value: 'medium' },
  { label: '高', value: 'high' },
  { label: '緊急', value: 'urgent' }
];

// ソート可能なテーブル行コンポーネント
interface SortableTableRowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  'data-row-key': string;
}

const SortableTableRow: React.FC<SortableTableRowProps> = ({ children, ...props }) => {
  const id = props['data-row-key'];
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style: React.CSSProperties = {
    ...props.style,
    transform: CSS.Transform.toString(transform && { ...transform, scaleY: 1 }),
    transition,
    ...(isDragging ? { position: 'relative', zIndex: 9999, opacity: 0.8 } : {}),
  };

  return (
    <tr {...props} ref={setNodeRef} style={style}>
      {React.Children.map(children, (child) => {
        if ((child as React.ReactElement)?.key === 'drag-handle') {
          return React.cloneElement(child as React.ReactElement, {
            children: (
              <HolderOutlined
                {...attributes}
                {...listeners}
                style={{ cursor: 'grab', color: '#999', touchAction: 'none' }}
              />
            ),
          });
        }
        return child;
      })}
    </tr>
  );
};

export function WorkOrderTemplatesPage(): JSX.Element {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const { canEdit, canManage, getDisabledMessage } = usePermission();
  const utils = trpc.useUtils();
  const [includeInactive, setIncludeInactive] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | undefined>(undefined);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<TemplateRecord | null>(null);
  const [checklistItems, setChecklistItems] = useState<ChecklistItem[]>([]);
  const [form] = Form.useForm();

  const { templates, isLoading, refetch } = useWorkOrderTemplates({
    includeInactive,
    includeEquipments: false
  });

  const { createTemplate, updateTemplate, deleteTemplate, isCreating, isUpdating, isDeleting } =
    useWorkOrderTemplateMutations();

  // dnd-kit センサー設定
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5, // 5pxドラッグしてから開始（クリックと区別）
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // 並び替えmutation（楽観的更新を実装）
  const reorderMutation = trpc.workOrderTemplate.reorder.useMutation({
    onMutate: async (variables) => {
      const items =
        (variables as { items?: { id?: string; orderIndex?: number }[] } | undefined)?.items ?? [];
      if (items.length === 0) {
        return undefined;
      }
      // 進行中のクエリをキャンセル
      await utils.workOrderTemplate.list.cancel();

      // 現在のデータを保存（ロールバック用）
      const previousData = utils.workOrderTemplate.list.getData({
        includeInactive,
        includeEquipments: false,
      });

      // キャッシュを即座に更新（楽観的更新）
      if (previousData) {
        const updatedData = [...previousData].sort((a, b) => {
          const aItem = items.find(item => item.id === a.id);
          const bItem = items.find(item => item.id === b.id);
          return (aItem?.orderIndex ?? 0) - (bItem?.orderIndex ?? 0);
        });

        utils.workOrderTemplate.list.setData(
          { includeInactive, includeEquipments: false },
          updatedData
        );
      }

      return { previousData };
    },
    onError: (_error, _variables, context) => {
      // エラー時はロールバック
      if (context?.previousData) {
        utils.workOrderTemplate.list.setData(
          { includeInactive, includeEquipments: false },
          context.previousData
        );
      }
      message.error('表示順序の更新に失敗しました');
    },
    onSuccess: () => {
      message.success('表示順序を更新しました');
    },
    onSettled: () => {
      // 最終的にサーバーと同期
      utils.workOrderTemplate.list.invalidate();
    },
  });

  // 複製mutation
  const duplicateMutation = trpc.workOrderTemplate.duplicate.useMutation({
    onSuccess: (data) => {
      message.success(`テンプレートを複製しました: ${data.templateName}`);
      refetch();
      // 複製したテンプレートの詳細ページへ遷移
      navigate(`/app/work-order-templates/${data.id}`);
    },
    onError: (error) => {
      message.error(`複製に失敗しました: ${error.message}`);
    },
  });

  // 作業カテゴリ一覧を取得
  const { data: workTypeCategories = [] } = trpc.workTypeCategory.list.useQuery({
    includeInactive: false,
    sortBy: 'display_order',
    sortOrder: 'asc',
  });

  const workTypeCategoryList = useMemo<WorkTypeCategorySummary[]>(() => {
    let list: unknown[] = [];
    if (Array.isArray(workTypeCategories)) {
      list = workTypeCategories;
    } else if (workTypeCategories && typeof workTypeCategories === 'object') {
      const nested = (workTypeCategories as { data?: unknown }).data;
      if (Array.isArray(nested)) {
        list = nested;
      }
    }
    return list.filter(isWorkTypeCategorySummary);
  }, [workTypeCategories]);

  const workTypeCategoryMap = useMemo(() => {
    const map = new Map<string, { categoryLabel?: string | null; color?: string | null }>();
    for (const category of workTypeCategoryList) {
      if (category?.id) {
        map.set(category.id, {
          categoryLabel: category.categoryLabel ?? '',
          color: category.color ?? undefined,
        });
      }
    }
    return map;
  }, [workTypeCategoryList]);

  const parseCommaSeparated = (value?: string) => {
    if (!value) {
      return undefined;
    }
    const items = value
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
    return items.length > 0 ? items : undefined;
  };

  const filteredTemplates = useMemo(() => {
    let filtered = templates;

    // カテゴリフィルタ
    if (selectedCategory) {
      filtered = filtered.filter((tpl) => tpl.workTypeCategoryId === selectedCategory);
    }

    // 検索フィルタ
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter((tpl) => {
        const categoryMeta =
          'workTypeCategory' in tpl && tpl.workTypeCategory
            ? tpl.workTypeCategory
            : tpl.workTypeCategoryId
              ? workTypeCategoryMap.get(tpl.workTypeCategoryId)
              : undefined;
        const categoryLabel = categoryMeta?.categoryLabel ?? undefined;
        const legacyWorkType =
          'workType' in tpl
            ? ((tpl as { workType?: string | null }).workType ?? undefined)
            : undefined;
        return [tpl.displayId, tpl.templateName, categoryLabel, legacyWorkType]
          .filter((value): value is string => Boolean(value))
          .some((value) => value.toLowerCase().includes(term));
      });
    }

    return filtered;
  }, [templates, searchTerm, selectedCategory, workTypeCategoryMap]);

  // フィルタ適用中かどうか（ドラッグ&ドロップの無効化判定用）
  const isFiltered = Boolean(searchTerm || selectedCategory);

  // ドラッグ終了時の処理
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    // 編集権限チェック
    if (!canEdit) {
      message.warning('閲覧権限のため、並び替えはできません');
      return;
    }

    // フィルタ適用中は並び替え無効
    if (isFiltered) {
      message.warning('フィルタを解除してから並び替えを行ってください');
      return;
    }

    const oldIndex = templates.findIndex((t) => t.id === active.id);
    const newIndex = templates.findIndex((t) => t.id === over.id);

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    const newOrder = arrayMove(templates, oldIndex, newIndex);

    // 新しい表示順序を計算してAPIに送信
    const reorderItems = newOrder.map((t, index) => ({
      id: t.id,
      orderIndex: index,
    }));

    reorderMutation.mutate({ items: reorderItems });
  };

  const handleOpenCreate = () => {
    setEditingTemplate(null);
    setIsModalOpen(true);
  };

  const handleOpenEdit = (record: TemplateRecord) => {
    setEditingTemplate(record);
    setIsModalOpen(true);
  };

  const handleDelete = (record: TemplateRecord) => {
    Modal.confirm({
      title: 'テンプレートを削除しますか？',
      content: `${record.templateName} を削除すると復元できません。`,
      okText: '削除する',
      okType: 'danger',
      cancelText: 'キャンセル',
      onOk: () => {
        deleteTemplate(record.id);
      }
    });
  };

  useEffect(() => {
    if (!isModalOpen) {
      return;
    }

    if (editingTemplate) {
      form.setFieldsValue({
        templateName: editingTemplate.templateName,
        description: editingTemplate.description ?? undefined,
        orderIndex: editingTemplate.orderIndex ?? 0,
        workTypeCategoryId: editingTemplate.workTypeCategoryId ?? undefined,
        priority: editingTemplate.priority,
        instructions: editingTemplate.instructions ?? undefined,
        safetyNotes: editingTemplate.safetyNotes ?? undefined
      });
      setChecklistItems((editingTemplate.checklistItems as ChecklistItem[]) || []);
    } else {
      form.resetFields();
      setChecklistItems([]);
    }
  }, [editingTemplate, form, isModalOpen]);

  const handleSubmit = async () => {
    const values = await form.validateFields();

    const payload = {
      templateName: values.templateName,
      description: values.description?.trim() ? values.description.trim() : undefined,
      orderIndex: typeof values.orderIndex === 'number' ? values.orderIndex : 0,
      workTypeCategoryId: values.workTypeCategoryId ?? undefined,
      priority: values.priority,
      instructions: values.instructions?.trim() ? values.instructions.trim() : undefined,
      safetyNotes: values.safetyNotes?.trim() ? values.safetyNotes.trim() : undefined,
      standardHours: typeof values.standardHours === 'number' ? values.standardHours : undefined,
      requiredTools: parseCommaSeparated(values.requiredTools),
      requiredParts: parseCommaSeparated(values.requiredParts),
      isActive: true,
      checklistItems: checklistItems.length > 0 ? checklistItems : undefined
    };

    if (editingTemplate) {
      updateTemplate({
        id: editingTemplate.id,
        ...payload
      });
    } else {
      createTemplate(payload);
    }

    setIsModalOpen(false);
  };

  const isDragDisabled = isFiltered || !canEdit;
  const getDragTooltip = () => {
    if (!canEdit) return '閲覧権限のため並び替えできません';
    if (isFiltered) return 'フィルタを解除すると並び替え可能';
    return '並び替え';
  };

  const columns: ColumnsType<TemplateRecord> = [
    {
      key: 'drag-handle',
      width: 40,
      align: 'center',
      render: () => (
        <Tooltip title={getDragTooltip()}>
          <HolderOutlined
            style={{
              cursor: isDragDisabled ? 'not-allowed' : 'grab',
              color: isDragDisabled ? '#d9d9d9' : '#999',
            }}
          />
        </Tooltip>
      ),
    },
    {
      title: '名前',
      dataIndex: 'templateName',
      key: 'templateName',
      ellipsis: true,
      render: (text: string, record) => (
        <Button
          type="link"
          onClick={() => navigate(`/app/work-order-templates/${record.id}`)}
          style={{ padding: 0 }}
        >
          {text}
        </Button>
      )
    },
    {
      title: '作業カテゴリ',
      dataIndex: 'workType',
      key: 'workType',
      render: (_, record) => {
        const categoryId =
          'workTypeCategoryId' in record
            ? (record as { workTypeCategoryId?: string | null }).workTypeCategoryId ?? undefined
            : undefined;
        const categoryMeta =
          'workTypeCategory' in record && record.workTypeCategory
            ? record.workTypeCategory
            : categoryId
              ? workTypeCategoryMap.get(categoryId)
              : undefined;
        if (categoryMeta?.categoryLabel) {
          return (
            <Tag color={categoryMeta.color ?? 'default'}>
              {categoryMeta.categoryLabel}
            </Tag>
          );
        }
        const legacyValue =
          'workType' in record ? (record as { workType?: string | null }).workType ?? undefined : undefined;
        const option = WORK_TYPE_OPTIONS.find((opt) => opt.value === legacyValue);
        return option?.label ?? legacyValue ?? '-';
      }
    },
    {
      title: '優先度',
      dataIndex: 'priority',
      key: 'priority',
      render: (value: string) => {
        const option = PRIORITY_OPTIONS.find((opt) => opt.value === value);
        return option?.label ?? value;
      }
    },
    {
      title: '更新日時',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (value: string | Date | undefined) => {
        if (!value) {
          return '-';
        }
        const date = value instanceof Date ? value : new Date(value);
        return date.toLocaleString();
      }
    },
    {
      title: '操作',
      key: 'actions',
      width: 160,
      align: 'center',
      render: (_, record) => (
        <Space size="small">
          {canEdit ? (
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleOpenEdit(record)}
            />
          ) : (
            <Tooltip title={getDisabledMessage('edit')}>
              <Button
                type="link"
                icon={<LockOutlined />}
                disabled
                style={{ color: '#d9d9d9' }}
              />
            </Tooltip>
          )}
          {canEdit ? (
            <Button
              type="link"
              icon={<CopyOutlined />}
              onClick={() => duplicateMutation.mutate({ id: record.id })}
              loading={duplicateMutation.isPending}
            />
          ) : (
            <Tooltip title={getDisabledMessage('edit')}>
              <Button
                type="link"
                icon={<CopyOutlined />}
                disabled
                style={{ color: '#d9d9d9' }}
              />
            </Tooltip>
          )}
          {canManage ? (
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              loading={isDeleting}
              onClick={() => handleDelete(record)}
            />
          ) : (
            <Tooltip title={getDisabledMessage('manage')}>
              <Button
                type="link"
                icon={<DeleteOutlined />}
                disabled
                style={{ color: '#d9d9d9' }}
              />
            </Tooltip>
          )}
        </Space>
      )
    }
  ];

  return (
    <div>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Space align="center" style={{ width: '100%', justifyContent: 'space-between' }}>
          <Space>
            <Title level={3} style={{ margin: 0 }}>
              作業指示書
            </Title>
            <PermissionButton
              icon={<SettingOutlined />}
              onClick={() => navigate('/app/categories#work-type')}
              title="作業カテゴリ管理"
              requiredPermission="manage"
            >
              カテゴリ管理
            </PermissionButton>
          </Space>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => void refetch()} />
            <PermissionButton
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleOpenCreate}
              requiredPermission="edit"
            >
              新規作成
            </PermissionButton>
          </Space>
        </Space>

        {/* 検索・フィルタ */}
        <Card style={{ marginBottom: 16 }}>
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col xs={24} sm={12} md={8}>
              <Input.Search
                allowClear
                placeholder="作業指示書名で検索"
                onSearch={setSearchTerm}
                style={{ width: '100%' }}
              />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Space>
                <Switch
                  checked={includeInactive}
                  onChange={setIncludeInactive}
                />
                <span>無効含む</span>
              </Space>
            </Col>
          </Row>

          {/* カテゴリタグクラウドフィルタ */}
          <CategoryTagCloudFilter
            categories={workTypeCategoryList.map(cat => ({
              id: cat.id,
              categoryLabel: cat.categoryLabel,
              color: cat.color,
              icon: cat.icon,
              count: templates.filter(t => t.workTypeCategoryId === cat.id).length,
            }))}
            selectedCategory={selectedCategory}
            onCategoryChange={setSelectedCategory}
            loading={!workTypeCategoryList.length && isLoading}
            showCounts={true}
            maxVisibleTags={15}
          />
        </Card>

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={filteredTemplates.map((t) => t.id)}
            strategy={verticalListSortingStrategy}
            disabled={isDragDisabled}
          >
            <Table<TemplateRecord>
              rowKey="id"
            loading={isLoading || reorderMutation.isPending}
              columns={columns}
              dataSource={filteredTemplates}
              pagination={{
                pageSize: 10,
                showSizeChanger: false
              }}
              components={{
                body: {
                  row: SortableTableRow,
                },
              }}
            />
          </SortableContext>
        </DndContext>
      </Space>

      <Modal
        title={editingTemplate ? 'テンプレート編集' : '作業指示書作成'}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        onOk={() => void handleSubmit()}
        okButtonProps={{
          loading: isCreating || isUpdating
        }}
        destroyOnHidden
        width={720}
      >
        <WorkOrderTemplateForm
          form={form}
          checklistItems={checklistItems}
          onChecklistItemsChange={setChecklistItems}
          workTypeCategories={workTypeCategoryList}
          showApprovalSettings={false}
        />
      </Modal>
    </div>
  );
}

